import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Download, Trash2, AlertTriangle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const { user, loading, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { state: { from: "/account" } });
  }, [user, loading, navigate]);

  const exportData = async () => {
    try {
      const { data } = await axios.get(`${API}/account/export`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "auto-advisor-my-data.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Download avviato");
    } catch {
      toast.error("Errore export");
    }
  };

  const deleteAccount = async () => {
    if (confirm !== "ELIMINA") {
      toast.error("Digita ELIMINA per confermare");
      return;
    }
    setBusy(true);
    try {
      await axios.delete(`${API}/account`, { withCredentials: true });
      setUser(null);
      toast.success("Account eliminato");
      navigate("/");
    } catch {
      toast.error("Errore eliminazione");
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen" data-testid="account-page">
      <header className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="font-heading text-xl font-semibold tracking-tight">
          auto<span className="text-[#0055FF]">.</span>advisor
        </Link>
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#666] hover:text-[#111]">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>
      </header>
      <main className="max-w-3xl mx-auto px-6 pt-6 pb-24">
        <h1 className="font-heading text-4xl font-semibold tracking-tight">I miei dati</h1>
        <p className="mt-2 text-[#666]">Gestisci il tuo account ed esercita i diritti previsti dal GDPR.</p>

        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-10 rounded-2xl border border-[#EAEAEA] bg-white p-6">
          <h2 className="font-heading text-xl font-medium">Profilo</h2>
          <dl className="mt-4 text-sm space-y-2">
            <div className="flex justify-between border-b border-[#F2F4F8] py-2"><dt className="text-[#666]">Email</dt><dd data-testid="account-email">{user.email}</dd></div>
            <div className="flex justify-between border-b border-[#F2F4F8] py-2"><dt className="text-[#666]">Nome</dt><dd>{user.name}</dd></div>
            <div className="flex justify-between py-2"><dt className="text-[#666]">Provider</dt><dd className="capitalize">{user.provider}</dd></div>
          </dl>
          <button onClick={logout} className="mt-6 h-10 px-5 rounded-full border border-[#EAEAEA] text-sm font-medium hover:border-[#111]" data-testid="account-logout">
            Esci
          </button>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="mt-6 rounded-2xl border border-[#EAEAEA] bg-white p-6">
          <h2 className="font-heading text-xl font-medium">Esporta i tuoi dati</h2>
          <p className="mt-2 text-sm text-[#666]">Scarica una copia in JSON di account, ricerche e lead associati alla tua email (GDPR art. 20).</p>
          <button onClick={exportData} className="mt-4 h-10 px-5 rounded-full bg-[#111] text-white text-sm font-medium hover:bg-[#333] inline-flex items-center gap-2" data-testid="account-export">
            <Download className="w-4 h-4" /> Scarica i miei dati (JSON)
          </button>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-6 rounded-2xl border border-red-200 bg-red-50/40 p-6">
          <h2 className="font-heading text-xl font-medium text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Elimina account
          </h2>
          <p className="mt-2 text-sm text-[#666]">
            Elimina definitivamente il tuo account, le tue ricerche, i lead con la tua email e le sessioni attive (GDPR art. 17). <b>Operazione irreversibile.</b>
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder='Digita "ELIMINA" per confermare'
              className="h-10 px-4 rounded-md border border-red-200 bg-white text-sm flex-1"
              data-testid="account-delete-confirm-input"
            />
            <button
              onClick={deleteAccount}
              disabled={busy || confirm !== "ELIMINA"}
              className="h-10 px-5 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              data-testid="account-delete-button"
            >
              <Trash2 className="w-4 h-4" /> Elimina definitivamente
            </button>
          </div>
        </motion.section>
      </main>
    </div>
  );
}
